import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AttioNotesService } from 'dashboard409a/services/attio/AttioNotesService';
import type {
  AnalystNoteHistoryItem,
  AnalystNoteReferenceType,
  WorkboardTaskOccurrenceReference,
} from './AnalystNotesHistoryPanel.types';

export const analystNotesHistoryQueryKey = (
  referenceType: AnalystNoteReferenceType,
  referenceId: string | number | undefined | null,
  workboardTaskOccurrence?: WorkboardTaskOccurrenceReference
) =>
  referenceType === 'wo'
    ? ([
      'analyst-notes-history',
      referenceType,
      workboardTaskOccurrence?.workboardTaskId,
      workboardTaskOccurrence?.occurrenceDate,
    ] as const)
    : (['analyst-notes-history', referenceType, referenceId] as const);

export const useAnalystNotesHistoryQuery = (
  referenceType: AnalystNoteReferenceType,
  referenceId: string | number | undefined | null,
  workboardTaskOccurrence?: WorkboardTaskOccurrenceReference
) => {
  const notesService = useMemo(() => new AttioNotesService(), []);
  const queryKey = useMemo(
    () => analystNotesHistoryQueryKey(referenceType, referenceId, workboardTaskOccurrence),
    [referenceType, referenceId, workboardTaskOccurrence]
  );

  const isWorkboardOccurrenceReady =
    referenceType === 'wo' &&
    workboardTaskOccurrence?.workboardTaskId != null &&
    Boolean(workboardTaskOccurrence.occurrenceDate);

  return useQuery<AnalystNoteHistoryItem[]>({
    queryKey,
    enabled:
      referenceType === 'wo'
        ? isWorkboardOccurrenceReady
        : referenceId !== undefined && referenceId !== null,
    retry: false,
    refetchOnWindowFocus: false,
    queryFn: () => {
      if (referenceType === 'wo') {
        if (!workboardTaskOccurrence) {
          return Promise.resolve([]);
        }
        return notesService.getNotesHistoryForWorkboardTaskOccurrence(workboardTaskOccurrence);
      }

      if (referenceId === undefined || referenceId === null) {
        return Promise.resolve([]);
      }
      return notesService.getNotesHistoryForReference(referenceType, referenceId);
    },
  });
};
